import { useState } from 'react';
import { TrendingUp, TrendingDown, Minus, Dumbbell, Utensils, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { useComplianceScore, ComplianceScore, ComplianceTrend } from '@/hooks/use-compliance-score';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface ComplianceScoreCardProps {
  clientId: string;
  className?: string;
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-success';
  if (score >= 60) return 'text-warning';
  if (score >= 40) return 'text-warning';
  return 'text-destructive';
}

function getScoreBgColor(score: number): string {
  if (score >= 80) return 'bg-success/10 border-success/30';
  if (score >= 60) return 'bg-warning/10 border-warning/30';
  if (score >= 40) return 'bg-warning/10 border-warning/30';
  return 'bg-destructive/10 border-destructive/30';
}

function getTrendIcon(trend: ComplianceTrend) {
  switch (trend) {
    case 'improving':
      return <TrendingUp className="h-4 w-4 text-success" />;
    case 'declining':
      return <TrendingDown className="h-4 w-4 text-destructive" />;
    default:
      return <Minus className="h-4 w-4 text-muted-foreground" />;
  }
}

function getTrendLabel(trend: ComplianceTrend): string {
  switch (trend) {
    case 'improving':
      return 'Improving';
    case 'declining':
      return 'Declining';
    default:
      return 'Stable';
  }
}

export function ComplianceScoreCard({ clientId, className }: ComplianceScoreCardProps) {
  const [periodDays, setPeriodDays] = useState(7);
  const { score, loading, error } = useComplianceScore(clientId, { periodDays });

  if (loading) {
    return (
      <Card className={cn('border', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-32" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !score) {
    return null;
  }

  return (
    <Card className={cn('border', getScoreBgColor(score.overallScore), className)}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <Activity className={cn('h-5 w-5', getScoreColor(score.overallScore))} />
            Compliance Score
          </span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              {getTrendIcon(score.trend)}
              <span>{getTrendLabel(score.trend)}</span>
            </div>
            <span className={cn('text-3xl font-bold', getScoreColor(score.overallScore))}>
              {score.overallScore}%
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Period selector */}
        <div className="flex gap-1">
          {[7, 14, 30].map(days => (
            <Button
              key={days}
              variant={periodDays === days ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPeriodDays(days)}
              className="text-xs"
            >
              {days === 7 ? '7 Days' : days === 14 ? '14 Days' : '30 Days'}
            </Button>
          ))}
        </div>

        {/* Circular score display */}
        <div className="flex items-center justify-center py-2">
          <div className="relative w-28 h-28">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="56"
                cy="56"
                r="48"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                className="text-muted"
              />
              <circle
                cx="56"
                cy="56"
                r="48"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${(score.overallScore / 100) * 301.6} 301.6`}
                className={getScoreColor(score.overallScore)}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={cn('text-2xl font-bold', getScoreColor(score.overallScore))}>
                {score.overallScore}%
              </span>
              <span className="text-xs text-muted-foreground">Overall</span>
            </div>
          </div>
        </div>

        {/* Breakdown */}
        <div className="space-y-3">
          {/* Workout Compliance */}
          <div>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="flex items-center gap-2 text-muted-foreground">
                <Dumbbell className="h-4 w-4" />
                Workouts
              </span>
              <span className="font-medium">
                {score.completedSessions}/{score.scheduledSessions} ({score.workoutCompliance}%)
              </span>
            </div>
            <Progress
              value={score.workoutCompliance}
              className="h-2"
            />
          </div>

          {/* Nutrition Compliance */}
          <div>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="flex items-center gap-2 text-muted-foreground">
                <Utensils className="h-4 w-4" />
                Nutrition Logging
              </span>
              <span className="font-medium">
                {score.daysWithMeals}/{score.totalDays} days ({score.nutritionCompliance}%)
              </span>
            </div>
            <Progress
              value={score.nutritionCompliance}
              className="h-2"
            />
          </div>
        </div>

        {/* Quick insight */}
        {score.overallScore < 60 && (
          <p className="text-sm text-muted-foreground bg-card/50 p-2 rounded">
            {score.workoutCompliance < score.nutritionCompliance
              ? 'Workout attendance needs attention. Consider checking in about scheduling.'
              : 'Nutrition logging is low. A quick reminder might help.'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
